// Territory.tsx — Empire borders on the galaxy map: a soft owner-coloured halo around every
// star with a world an empire holds (the largest holder's colour when contested), plus a
// hollow ring on stars that still have independent worlds worth taking. Subscribes to the
// sim snapshot but only rebuilds when ownership actually changes.
import { useMemo } from 'react';
import { AdditiveBlending, CanvasTexture, SRGBColorSpace } from 'three';
import type { Galaxy } from '../types';
import { useSim } from '../state/context';

const HALO = 3.2; // world units

let ringTex: CanvasTexture | null = null;

// A soft-edged annulus: reads as a border around the star without hiding it.
function ringTexture(): CanvasTexture {
  if (ringTex) return ringTex;
  const size = 128;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');
  if (ctx) {
    const r = size / 2;
    const g = ctx.createRadialGradient(r, r, 0, r, r, r);
    g.addColorStop(0, 'rgba(255,255,255,0.10)');
    g.addColorStop(0.62, 'rgba(255,255,255,0.14)');
    g.addColorStop(0.78, 'rgba(255,255,255,0.95)');
    g.addColorStop(0.88, 'rgba(255,255,255,0.35)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
  }
  ringTex = new CanvasTexture(c);
  ringTex.colorSpace = SRGBColorSpace;
  return ringTex;
}

export function Territory({ galaxy }: { galaxy: Galaxy }) {
  const sim = useSim();
  const glow = useMemo(ringTexture, []);
  // A stable key of who holds what, so React only re-renders on a real change.
  const key = (sim?.worlds ?? [])
    .filter((w) => w.owner)
    .map((w) => `${w.planetId}:${w.owner}`)
    .join(',');
  const halos = useMemo(() => {
    if (!sim) return [];
    const bySys = new Map<string, Map<string, number>>();
    for (const w of sim.worlds ?? []) {
      if (!w.owner) continue;
      const m = bySys.get(w.systemId) ?? new Map<string, number>();
      m.set(w.owner, (m.get(w.owner) ?? 0) + w.pops + 1);
      bySys.set(w.systemId, m);
    }
    const out: { pos: [number, number, number]; color: string; id: string }[] = [];
    for (const [sysId, m] of bySys) {
      const sys = galaxy.systems.find((s) => s.id === sysId);
      if (!sys) continue;
      let best = '';
      let bp = -1;
      for (const [o, p] of m) if (p > bp) [best, bp] = [o, p];
      const color = sim.empires?.find((e) => e.id === best)?.color ?? '#ffffff';
      out.push({ pos: sys.position, color, id: sysId });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, galaxy, sim?.empires?.length]);
  if (!sim || halos.length === 0) return null;
  return (
    <>
      {halos.map((h) => (
        <sprite key={h.id} position={h.pos} scale={[HALO, HALO, 1]} renderOrder={0} raycast={() => null}>
          <spriteMaterial map={glow} color={h.color} transparent opacity={0.8} depthWrite={false} blending={AdditiveBlending} toneMapped={false} />
        </sprite>
      ))}
    </>
  );
}
